/**
 * 생육 3D 분석 자료 — grosync-lrm 이 생육 사진 옆에 두는 파일을 읽는다.
 *
 *   growth/{deviceId}/{YYYY-MM}/{YYYYMMDD}.jpg          사진 (API 가 저장)
 *   growth/{deviceId}/{YYYY-MM}/{YYYYMMDD}.glb          원본 3D 모델 (다운로드용)
 *   growth/{deviceId}/{YYYY-MM}/{YYYYMMDD}_height.glb   초장 보기 3D
 *   growth/{deviceId}/{YYYY-MM}/{YYYYMMDD}_leaf.glb     엽면적 보기 3D
 *   growth/{deviceId}/{YYYY-MM}/{YYYYMMDD}.json         결과 + 화면 보기 정의 — 있으면 그날 3D 처리 완료
 *
 * 파일은 모두 GET /api/growth/uploads/* (JWT) 로 받는다. <img>·<model-viewer> 는 Authorization
 * 헤더를 못 붙이므로 fetch 로 받아 blob URL 로 넘긴다. 형식: docs/ai-growth-analysis.md
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { apiGetBlob } from '../../api/client';
import { getPhotoUrl } from '../../api/growth';

export interface MeasureHotspot {
  key: string;
  label: string;
  rgb: number[];
  position_m: number[];
  normal: number[];
  style?: string;
  side?: string;
}

/** 한 날짜의 3D 보기 하나 (초장 · 엽면적) — 같은 포기·같은 좌표, 표시만 다르다 */
export interface Growth3dView {
  key: string;
  label: string;
  file: string;
  model_note?: string;
  ghost_default?: boolean;
  /** 반투명일 때 포기 불투명도 (기본 0.38) */
  ghost_alpha?: number;
  measure_hotspots?: MeasureHotspot[];
}

export interface StemSummary {
  length_mm: number;
  segments_mm: number[];
  rgb: number[];
  warnings?: string[];
}

export interface Growth3dResult {
  device_id: string;
  date: string;
  photo: string;
  /** 처리한 사진의 해시 — 그 뒤 같은 날 사진을 덮어썼으면 지금 사진과 다르다 */
  photo_sha256: string | null;
  finished?: string;
  files: { model?: string; height_view?: string; leaf_view?: string };
  height: {
    height_mm: number | null;
    version?: string;
    segments_mm?: number[] | null;
    quality?: { status?: string; reasons?: string[] } | null;
    warnings?: string[];
    component_qc?: Record<string, unknown> | null;
    /** height_mm 가 null 인 이유 */
    error?: string | null;
  };
  leaf_area?: {
    leaf_area_cm2: number | null;
    confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
    low_share?: number | null;
    low_reasons?: string[] | null;
    n_compound?: number | null;
    n_leaflet?: number | null;
  };
  photo_measure?: {
    height_mm_photo?: number | null;
    canopy_width_mm?: number | null;
    crown_px?: number[] | null;
    marker?: Record<string, unknown> | null;
  };
  viewer?: {
    camera_orbit?: string;
    camera_target?: string;
    field_of_view?: string;
    stem_summary?: StemSummary | null;
    views?: Growth3dView[];
  };
}

/** 사진 photo_path(growth/…/{YYYYMMDD}.jpg) 옆 파일의 API 경로 */
export function siblingUrl(photoPath: string, file: string): string {
  const dir = photoPath.slice(0, photoPath.lastIndexOf('/') + 1);
  return getPhotoUrl(`${dir}${file}`);
}

/** 그날 결과 json. 404 = 아직 3D 처리 전 → null */
export async function fetchGrowth3dResult(photoPath: string): Promise<Growth3dResult | null> {
  const token = useAuthStore.getState().token;
  const res = await fetch(getPhotoUrl(photoPath.replace(/\.[^./]+$/, '.json')), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: 'no-cache',
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`3D 결과를 읽지 못했습니다 (HTTP ${res.status})`);
  return (await res.json()) as Growth3dResult;
}

/* ---- 인증 파일 → blob URL ------------------------------------------------ */

// 같은 파일을 보기 전환·날짜 왕복마다 다시 받지 않게 최근 것만 들고 있는다. 밀려난 URL 은 해제한다.
// GLB 는 수 MB 라 모델용은 작게, 사진은 타임라인 재생용으로 넉넉히.
interface BlobCache {
  kind: 'model' | 'photo';
  limit: number;
  map: Map<string, Promise<string>>;
}
const MODEL_CACHE: BlobCache = { kind: 'model', limit: 6, map: new Map() };
const PHOTO_CACHE: BlobCache = { kind: 'photo', limit: 90, map: new Map() };
// 해제한 URL 을 쿼리 캐시가 계속 들고 있지 않게 — 밀려난 파일의 쿼리도 지운다 (useAuthedUrl 이 등록)
let onEvict: ((kind: BlobCache['kind'], url: string) => void) | null = null;

function cachedBlobUrl(cache: BlobCache, url: string): Promise<string> {
  const hit = cache.map.get(url);
  if (hit) {
    cache.map.delete(url);
    cache.map.set(url, hit);
    return hit;
  }
  const p = apiGetBlob(url).then((b) => URL.createObjectURL(b));
  p.catch(() => cache.map.delete(url));
  cache.map.set(url, p);
  while (cache.map.size > cache.limit) {
    const [oldKey, old] = cache.map.entries().next().value as [string, Promise<string>];
    cache.map.delete(oldKey);
    onEvict?.(cache.kind, oldKey);
    void old.then(
      (u) => URL.revokeObjectURL(u),
      () => undefined,
    );
  }
  return p;
}

export function preloadPhoto(url: string): void {
  void cachedBlobUrl(PHOTO_CACHE, url).catch(() => undefined);
}

/** 파일 이름의 날짜 부분 (…/{YYYYMMDD}_leaf.glb → 장비 폴더 + YYYYMMDD) */
const dayKey = (url: unknown): string | null =>
  typeof url === 'string' ? (/^(.*\/\d{8})[^/]*$/.exec(url)?.[1] ?? null) : null;

/**
 * 인증이 필요한 파일을 blob URL 로. url 이 없거나 받는 중이면 src 는 null.
 * 모델은 같은 날짜의 다른 보기(초장 ↔ 엽면적)를 받는 동안 앞 보기를 그대로 준다(`pending`) —
 * 뷰어가 내려갔다 다시 뜨면 돌려 둔 시점이 사라지기 때문이다.
 */
export function useAuthedUrl(
  url: string | null | undefined,
  kind: 'model' | 'photo',
): { src: string | null; error: string | null; pending: boolean } {
  const qc = useQueryClient();
  onEvict = (k, u) => qc.removeQueries({ queryKey: ['growth3d-file', k, u], exact: true });
  const q = useQuery({
    queryKey: ['growth3d-file', kind, url],
    queryFn: () => cachedBlobUrl(kind === 'model' ? MODEL_CACHE : PHOTO_CACHE, url!),
    enabled: !!url,
    staleTime: Infinity,
    retry: false,
    placeholderData: (prev, prevQuery) =>
      kind === 'model' && prevQuery && dayKey(prevQuery.queryKey[2]) === dayKey(url)
        ? prev
        : undefined,
  });
  return {
    src: url ? (q.data ?? null) : null,
    error: q.error ? q.error.message : null,
    pending: q.isPlaceholderData,
  };
}

/** 인증 파일을 받아 저장한다 (GLB 다운로드) */
export async function downloadAuthed(url: string, filename: string): Promise<void> {
  const blob = await apiGetBlob(url);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** model-viewer 는 3D 분석 탭을 열 때만 불러온다 (three.js 포함 — 번들 분리) */
let viewerPromise: Promise<void> | null = null;
export function loadModelViewer(): Promise<void> {
  if (!viewerPromise) {
    viewerPromise = import('@google/model-viewer').then(
      () => undefined,
      (e: unknown) => {
        viewerPromise = null;
        throw e instanceof Error ? e : new Error('3D 뷰어를 불러오지 못했습니다.');
      },
    );
  }
  return viewerPromise;
}
